import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";

// Style
import {
  CompetitionsSearch,
  HomeSearch,
  HomeSearchContent,
  PlayersSearch,
  SearchForm,
  Search,
  TeamsSearch,
  ToggleSearch,
  SearchByFlag,
  SearchContent,
  SearchInput,
  SearchResult,
  HistoryList,
  HistoryHeader,
  WrapSearch,
  TeamTypeToggle,
  TeamTypeOption,
  ToggleSlot,
  ResultsFrame,
  Area,
  FagsWrap,
  FullAreaWrap,
  ChooseGrid,
  ListWrap,
  Flag,
  FlagList,
} from "../../style/HomeSearch";
import { MainWrapper } from "../../style/DasboardMain";

// Componenst
import Navigation from "../../components/Navigation/Navigation";
import Button from "../../components/button";
import VersionUpdateNotifier from "../../components/version/VersionUpdateNotifier";

// Images
import placeholder from "../../images/placeholderSDark.svg";
import placeholderLight from "../../images/placeholderSLight.svg";
import searchIcon from "../../images/ic_search.svg";
import closeIcon from "../../images/ic_close.svg";
import team from "../../images/team.webp";
import eyeOpen from "../../images/on.svg";
import eyeClosed from "../../images/off.svg";
import listLight from "../../images/listLightMode.png";
import listDark from "../../images/listDarkMode.png";
import gridLight from "../../images/gridLightMode.png";
import gridDark from "../../images/gridDarkMode.png";

import { $apiV1, $apiV2 } from "../../config/api";
import { Oval } from "react-loader-spinner";
import { useLexicon } from "../../hooks/useLexicon";
import { API_URL_V1 } from "../../config/api";
import { getMediaUrl } from "../../helpers";
import { useSelector } from "react-redux";
import {
  trackEntitySearch,
  trackEntitySearchResultSelected,
} from "../../tracking";

const ENTITY_TYPE_BY_TAB = {
  players: 1,
  teams: 2,
  competitions: 3,
};

const dashboardHistoryCache = {
  byKey: {},
  inFlightByKey: {},
};
const PAGE_HISTORY_UPDATED_EVENT = "pageHistoryChange";
const FLAG_PAGE_SIZE_STEP = 100;
const DASHBOARD_TUTORIAL_STORAGE_KEY = "dashboard_tutorial_seen_v1";
const _isLightTheme = localStorage.getItem("theme") === "light";
const DASHBOARD_TUTORIAL_ACCENT = _isLightTheme ? "#2363F7" : "#BEFF00";
const DASHBOARD_TUTORIAL_CARD_BG = _isLightTheme ? "#ffffff" : "linear-gradient(145deg, #1a2612, #131c0e)";
const DASHBOARD_TUTORIAL_TITLE_COLOR = _isLightTheme ? "#0f1117" : "#ffffff";
const DASHBOARD_TUTORIAL_BODY_COLOR = _isLightTheme ? "#6B7280" : "#8a9e80";
const DASHBOARD_TUTORIAL_SKIP_COLOR = _isLightTheme ? "#9CA3AF" : "#5a6e50";
const DASHBOARD_TUTORIAL_BTN_TEXT = _isLightTheme ? "#ffffff" : "#0a0e05";
const DASHBOARD_TUTORIAL_STEP_INACTIVE = _isLightTheme ? "#E5E7EB" : "#ffffff22";
const DASHBOARD_TUTORIAL_FONT_BASE =
  '"SatoshiRegular", "Satoshi-Regular", sans-serif';
const DASHBOARD_TUTORIAL_FONT_MEDIUM =
  '"SatoshiMedium", "SatoshiRegular", sans-serif';
const DASHBOARD_TUTORIAL_FONT_BOLD =
  '"SatoshiBold", "SatoshiMedium", "SatoshiRegular", sans-serif';
const DASHBOARD_TUTORIAL_STEPS = [
  {
    id: "welcome",
    tab: "teams",
    spotlight: null,
    title: "We've made some changes\nto our home page.",
    body: "Let us give you a quick tour of what's new and how to get the most out of the search experience.",
    position: "center",
  },
  {
    id: "tabs",
    tab: "teams",
    spotlight: "tabs",
    title: "Three search modes",
    body: "Switch between Teams, Players, and Competitions using these tabs. Each mode is tailored to help you find exactly what you need.",
    position: "below",
  },
  {
    id: "team-search",
    tab: "teams",
    spotlight: "search",
    title: "Search teams here",
    body: "Type any club or national team name. You can also toggle Flag Search to browse by country.",
    position: "below",
  },
  {
    id: "team-filter",
    tab: "teams",
    spotlight: "filters",
    title: "Filter by type",
    body: "Narrow results to Club teams or National sides with a single click.",
    position: "below",
  },
  {
    id: "team-history",
    tab: "teams",
    spotlight: "history",
    title: "Your team history",
    body: "Teams you've recently viewed will appear here for quick access. Clear history anytime with the icon above.",
    position: "above",
  },
  {
    id: "player-search",
    tab: "players",
    spotlight: "search",
    title: "Find your favourite players",
    body: "Switch to Players and search by athlete name. Country flags help you spot nationalities at a glance.",
    position: "below",
  },
  {
    id: "player-history",
    tab: "players",
    spotlight: "history",
    title: "Player history",
    body: "Your recently viewed players are saved here so you can jump back in seconds.",
    position: "above",
  },
  {
    id: "comp-search",
    tab: "competitions",
    spotlight: "search",
    title: "Browse competitions",
    body: "Head over to Competitions to explore leagues and tournaments from all around the world.",
    position: "below",
  },
  {
    id: "comp-history",
    tab: "competitions",
    spotlight: "history",
    title: "Competition history",
    body: "Leagues you've browsed recently show up here for instant access.",
    position: "above",
  },
];

const getHistoryUserScope = () => {
  const token = localStorage.getItem("token");
  if (!token || token === "undefined") return "guest";
  return `token:${String(token).slice(-12)}`;
};

const Index = () => {
  const [activeTab, setActiveTab] = useState("teams");
  const [text, setText] = useState("");
  const [toggleChecked, setToggleChecked] = useState(false);
  const [teamSearchText, setTeamSearchText] = useState("");
  const [teamPage, setTeamPage] = useState(1);
  const [playerPage, setPlayerPage] = useState(1);
  const [competitionPage, setCompetitionPage] = useState(1);
  const [selectedTeamType, setSelectedTeamType] = useState("1");
  const [teamSearchResults, setTeamSearchResults] = useState([]);
  const [teamTotalResults, setTeamTotalResults] = useState(0);
  const [playerSearchResults, setPlayerSearchResults] = useState([]);
  const [playerTotalResults, setPlayerTotalResults] = useState(0);
  const [competitionSearchResults, setCompetitionSearchResults] = useState([]);
  const [competitionTotalResults, setCompetitionTotalResults] = useState(0);
  const [flags, setFlags] = useState({ results: [], count: 0 });
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [flagLeague, setFlagLeague] = useState([]);
  const [flagSearchText, setFlagSearchText] = useState("");
  const [leagueSearchText, setLeagueSearchText] = useState("");
  const [flagSearchResults, setFlagSearchResults] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [teamLeagueSearch, setTeamLeagueSearch] = useState([]);
  const [listOption, setListOption] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [pageHistoryVersion, setPageHistoryVersion] = useState(0);
  const [historyList, setHistoryList] = useState({
    players: [],
    teams: [],
    competitions: [],
    default: [],
  });
  const [showhistoryList, setShowHistoryList] = useState(true);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showableHistory, setShowableHistory] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);
  const searchAbortRef = useRef(null);
  const latestSearchRequestRef = useRef(0);
  const latestFlagTeamRequestRef = useRef(0);
  const searchInputRef = useRef(null);
  const tabsRef = useRef(null);
  const searchFormRef = useRef(null);
  const filtersRef = useRef(null);
  const historyRef = useRef(null);

  const navigate = useNavigate();

  const l = useLexicon();
  const isMacLike =
    typeof navigator !== "undefined" &&
    /(Mac|iPhone|iPad|iPod)/i.test(navigator.platform);
  const activeTabIndex =
    activeTab === "teams" ? 0 : activeTab === "players" ? 1 : 2;
  const heroTitleRaw = l(1656) || l(903) || "";
  const heroSubtitle = "Search across all leagues, clubs and athletes";

  const buildHeroTitleLines = (value) => {
    const normalized = (value || "").trim();
    if (!normalized) {
      return {
        line1: "TEAMS, PLAYERS",
        line2: "OR COMPETITIONS",
      };
    }

    const withoutPrefix = normalized.replace(/^start searching(?: for)?\s+/i, "");
    const source = withoutPrefix || normalized;
    const matches = [...source.matchAll(/\s*,?\s+or\s+/gi)];
    if (matches.length === 0) {
      return { line1: source, line2: "" };
    }

    const last = matches[matches.length - 1];
    const index = last.index || 0;
    const secondPart = source.slice(index + last[0].length).trim();
    return {
      line1: source.slice(0, index).trim(),
      line2: secondPart ? `or ${secondPart}` : "",
    };
  };

  const { line1: heroTitleLine1, line2: heroTitleLine2 } = buildHeroTitleLines(
    heroTitleRaw
  );

  const isTruthySeasonFlag = (value) =>
    value === true || value === "true" || value === 1 || value === "1";

  const getSeasonValue = (season, keys) => {
    for (const key of keys) {
      if (season?.[key] !== undefined && season?.[key] !== null && season?.[key] !== "") {
        return season[key];
      }
      if (
        season?.year_season?.[key] !== undefined &&
        season?.year_season?.[key] !== null &&
        season?.year_season?.[key] !== ""
      ) {
        return season.year_season[key];
      }
    }
    return null;
  };

  const parseSeasonDate = (value) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const parseSeasonYears = (season) => {
    const values = [
      season?.year_season,
      season?.yearSeason,
      season?.title,
      season?.shortTitle,
    ];

    for (const value of values) {
      if (!value) continue;
      const normalized = String(value);
      const rangeMatch = normalized.match(/((?:19|20)\d{2})\s*[-/]\s*((?:19|20)\d{2})/);
      if (rangeMatch) {
        return {
          startYear: Number(rangeMatch[1]),
          endYear: Number(rangeMatch[2]),
        };
      }
      const yearMatch = normalized.match(/\b((?:19|20)\d{2})\b/);
      if (yearMatch) {
        const year = Number(yearMatch[1]);
        return {
          startYear: year,
          endYear: year,
        };
      }
    }

    return {
      startYear: Number.MIN_SAFE_INTEGER,
      endYear: Number.MIN_SAFE_INTEGER,
    };
  };

  const getSeasonSortMeta = (season) => {
    const endDate = parseSeasonDate(
      getSeasonValue(season, ["end_date", "date_end", "end", "ends_at"])
    );
    const startDate = parseSeasonDate(
      getSeasonValue(season, ["start_date", "date_start", "start", "starts_at"])
    );
    const { startYear, endYear } = parseSeasonYears(season);

    return {
      endDateTs: endDate ? endDate.getTime() : Number.MIN_SAFE_INTEGER,
      startDateTs: startDate ? startDate.getTime() : Number.MIN_SAFE_INTEGER,
      endYear,
      startYear,
      id: Number(season?.id || 0),
    };
  };

  const compareSeasonsDesc = (a, b) => {
    const metaA = getSeasonSortMeta(a);
    const metaB = getSeasonSortMeta(b);

    if (metaA.endDateTs !== metaB.endDateTs) {
      return metaB.endDateTs - metaA.endDateTs;
    }
    if (metaA.startDateTs !== metaB.startDateTs) {
      return metaB.startDateTs - metaA.startDateTs;
    }
    if (metaA.endYear !== metaB.endYear) {
      return metaB.endYear - metaA.endYear;
    }
    if (metaA.startYear !== metaB.startYear) {
      return metaB.startYear - metaA.startYear;
    }
    return metaB.id - metaA.id;
  };

  const getSeasonLabel = (season) => {
    const rawYearSeason = season?.year_season || season?.yearSeason;
    if (rawYearSeason) {
      return String(rawYearSeason).replace(/\s+/g, "");
    }

    const rawTitle = season?.title || season?.shortTitle || "";
    const rangeMatch = String(rawTitle).match(/((?:19|20)\d{2})\s*[-/]\s*((?:19|20)\d{2})/);
    if (rangeMatch) {
      return `${rangeMatch[1]}-${rangeMatch[2]}`;
    }

    const yearMatch = String(rawTitle).match(/\b((?:19|20)\d{2})\b/);
    if (yearMatch) {
      return yearMatch[1];
    }

    return `${season?.id || ""}`;
  };

  const getPreferredSeason = (seasons = []) => {
    const seasonsList = Array.isArray(seasons)
      ? seasons.filter((season) => season?.id !== undefined && season?.id !== null)
      : [];

    if (!seasonsList.length) return null;

    const activeCandidates = seasonsList.filter((season) =>
      ["is_active", "active", "is_current", "current"].some((flag) =>
        isTruthySeasonFlag(getSeasonValue(season, [flag]))
      )
    );

    if (activeCandidates.length > 0) {
      return [...activeCandidates].sort(compareSeasonsDesc)[0];
    }

    const now = new Date();
    const startedCandidates = seasonsList
      .filter((season) => {
        const startDate = parseSeasonDate(
          getSeasonValue(season, ["start_date", "date_start", "start", "starts_at"])
        );
        return !startDate || startDate <= now;
      })
      .sort(compareSeasonsDesc);

    if (startedCandidates.length > 0) {
      return startedCandidates[0];
    }

    const byRecencyDesc = [...seasonsList].sort(compareSeasonsDesc);
    return byRecencyDesc[0] || seasonsList[0];
  };

  const getSortedSeasons = (seasons = []) =>
    [...(seasons || [])].sort(compareSeasonsDesc);

  const openCompetitionSeason = (season, inNewTab = false, competition = null) => {
    const seasonId = typeof season === "object" ? season?.id : season;
    if (!seasonId) return;
    trackEntitySearchResultSelected({
      entity: competition,
      entityId: competition?.id || seasonId,
      entityLabel: competition?.name || competition?.title || null,
      entityType: "competition",
      query: text,
      sourceSurface: "dashboard",
      metadata: {
        season_id: seasonId,
        season_label: typeof season === "object" ? getSeasonLabel(season) : null,
      },
    });
    const url = `/tournaments/competition/${seasonId}`;
    if (inNewTab) {
      window.open(url, "_blank");
      return;
    }
    navigate(url);
  };

  const openTeam = (teamItem, metadata = {}, inNewTab = false) => {
    if (!teamItem?.id) return;
    trackEntitySearchResultSelected({
      entity: teamItem,
      entityType: "team",
      query: text || teamSearchText,
      sourceSurface: "dashboard",
      metadata,
    });
    const url = `/tournaments/team/${teamItem.id}`;
    if (inNewTab) {
      window.open(url, "_blank");
      return;
    }
    navigate(url);
  };

  const openPlayer = (player, inNewTab = false) => {
    if (!player?.id) return;
    trackEntitySearchResultSelected({
      entity: player,
      entityType: "player",
      query: text,
      sourceSurface: "dashboard",
    });
    const url = `/tournaments/player/${player.id}`;
    if (inNewTab) {
      window.open(url, "_blank");
      return;
    }
    navigate(url);
  };

  const getHistoryByTab = (tab, source = historyList) => {
    if (tab === "teams") return source?.teams || [];
    if (tab === "players") return source?.players || [];
    return source?.competitions || [];
  };

  const getHistoryEntityType = (item) =>
    Number(item?.entity_type ?? item?.entity?.entity_type ?? item?.entityType ?? 0);

  const getVisitedCompetitionSeasonIds = () => {
    try {
      const historyEntries = JSON.parse(localStorage.getItem("pageHistory") || "[]");
      const seasonIds = new Set();

      historyEntries.forEach((entry) => {
        const path = String(entry?.path || "");
        if (!path) return;

        const competitionMatch = path.match(/\/tournaments\/competition\/(\d+)/);
        if (competitionMatch?.[1]) {
          seasonIds.add(String(competitionMatch[1]));
        }

        const queryIndex = path.indexOf("?");
        if (queryIndex === -1) return;
        const search = path.slice(queryIndex + 1);
        const params = new URLSearchParams(search);
        const querySeasonId = params.get("seasonId");
        if (querySeasonId) {
          seasonIds.add(String(querySeasonId));
        }
      });

      return seasonIds;
    } catch (error) {
      return new Set();
    }
  };

  const getStrictHistoryCompetitionSeasonId = (item) => {
    const entity = item?.entity || {};
    if (entity?.season_id) return entity.season_id;
    if (item?.season_id) return item.season_id;

    const looksLikeSeasonEntity =
      entity?.year_season ||
      entity?.date_start ||
      entity?.date_end ||
      entity?.shortTitle;

    if (looksLikeSeasonEntity && entity?.id) return entity.id;
    return null;
  };

  const getHistoryCompetitionSeasonId = (item, visitedSeasonIds = new Set()) => {
    const strictSeasonId = getStrictHistoryCompetitionSeasonId(item);
    if (strictSeasonId) return strictSeasonId;

    if (!(visitedSeasonIds instanceof Set) || visitedSeasonIds.size === 0) {
      return null;
    }

    const seasons = Array.isArray(item?.entity?.seasons) ? item.entity.seasons : [];
    const matchedVisitedSeason = seasons.find((season) =>
      visitedSeasonIds.has(String(season?.id))
    );

    return matchedVisitedSeason?.id || null;
  };

  const visitedCompetitionSeasonIds = useMemo(
    () => getVisitedCompetitionSeasonIds(),
    [pageHistoryVersion]
  );

  const looksLikeCompetitionHistoryItem = (item, visitedSeasonIds) => {
    const entity = item?.entity || {};
    const isTeamLike = Boolean(entity?.team_type_display) || Boolean(entity?.team_type);
    const isPlayerLike = Boolean(entity?.surname) || Array.isArray(entity?.nationality);
    if (isTeamLike || isPlayerLike) return false;

    return Boolean(getHistoryCompetitionSeasonId(item, visitedSeasonIds));
  };

  const getHistoryCacheKey = (tab, teamType, userScope) =>
    tab === "teams"
      ? `${userScope}:${tab}:${String(teamType || "1")}`
      : `${userScope}:${tab}`;

  const getHistoryParamsByTab = (tab, teamType) => {
    if (tab === "teams") {
      return { entity: 2, team_type: String(teamType || "1") };
    }
    if (tab === "players") {
      return { entity: 1 };
    }
    return { entity: 3 };
  };

  const applyHistoryState = (tab, items) => {
    setHistoryList((prev) => ({
      ...prev,
      [tab]: items || [],
    }));
  };

  const fetchDashboardHistoryByTab = async (tab, teamType, options = {}) => {
    const { force = false, visitedSeasonIds = visitedCompetitionSeasonIds } = options;
    const cacheKey = getHistoryCacheKey(tab, teamType, getHistoryUserScope());

    if (!force && dashboardHistoryCache.byKey[cacheKey]) {
      return dashboardHistoryCache.byKey[cacheKey];
    }

    if (dashboardHistoryCache.inFlightByKey[cacheKey]) {
      return dashboardHistoryCache.inFlightByKey[cacheKey];
    }

    dashboardHistoryCache.inFlightByKey[cacheKey] = (async () => {
      try {
        const response = await $apiV2.get(`/users/history`, {
          params: getHistoryParamsByTab(tab, teamType),
        });
        const expectedEntityType = ENTITY_TYPE_BY_TAB[tab];
        let filtered = (response?.data?.results || []).filter((item) => {
          const entityType = getHistoryEntityType(item);
          // Backend may return null entity_type; in that case trust endpoint params.
          if (!entityType) return true;
          return entityType === expectedEntityType;
        });

        if (tab === "competitions") {
          filtered = filtered.filter((item) =>
            Boolean(getHistoryCompetitionSeasonId(item, visitedSeasonIds))
          );

          if (filtered.length === 0) {
            const fallbackResponse = await $apiV2.get(`/users/history`);
            filtered = (fallbackResponse?.data?.results || []).filter((item) =>
              looksLikeCompetitionHistoryItem(item, visitedSeasonIds)
            );
          }
        }

        dashboardHistoryCache.byKey[cacheKey] = filtered;
        return filtered;
      } catch (error) {
        console.error("Error fetching history:", error);
        return dashboardHistoryCache.byKey[cacheKey] || [];
      } finally {
        delete dashboardHistoryCache.inFlightByKey[cacheKey];
      }
    })();

    return dashboardHistoryCache.inFlightByKey[cacheKey];
  };

  const resetFlagFlowState = () => {
    latestFlagTeamRequestRef.current += 1;
    setSelectedFlag(null);
    setFlagLeague([]);
    setLeagueSearchText("");
    setSelectedLeague(null);
    setTeamLeagueSearch([]);
    setTeamSearchText("");
    setListOption(false);
    setFlagSearchResults([]);
  };

  const getFlags = async (currentPageSize = pageSize) => {
    try {
      setLoadingSearch(true);
      const response = await $apiV1.get(`/platform/nationality`, {
        params: {
          page_size: currentPageSize,
          with_tournaments: true,
        },
      });

      const results = Array.isArray(response?.data?.results)
        ? response.data.results
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setFlags({
        results,
        count: Number(response?.data?.count || results.length),
      });
    } catch (error) {
      console.error("Error fetching flags:", error);
      setFlags({ results: [], count: 0 });
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleToggleChange = () => {
    if (activeTab !== "teams") {
      setToggleChecked(false);
      return;
    }

    const next = !toggleChecked;
    latestFlagTeamRequestRef.current += 1;
    setToggleChecked(next);
    setText("");

    if (next) {
      setFlagSearchText("");
      resetFlagFlowState();
      if (!flags?.results?.length) {
        getFlags();
      }
      searchAbortRef.current?.abort();
      searchAbortRef.current = null;
      setLoadingSearch(false);
    } else {
      setFlagSearchText("");
      resetFlagFlowState();
    }
  };

  const handleFlagSearch = async (e) => {
    const searchText = e.target.value;
    setFlagSearchText(searchText);
    setSelectedFlag(null);
    setSelectedLeague(null);
    setFlagLeague([]);
    setTeamLeagueSearch([]);

    if (!searchText.trim()) {
      setFlagSearchResults([]);
      return;
    }

    try {
      setLoadingSearch(true);
      const response = await $apiV1.get(`/platform/nationality`, {
        params: {
          page: 1,
          search: searchText,
          page_size: pageSize,
          with_tournaments: true,
        },
      });
      setFlagSearchResults(response?.data?.results || []);
    } catch (error) {
      console.error("Error searching flags:", error);
      setFlagSearchResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleLeague = async (e) => {
    const searchLeagueText = e.target.value;
    setLeagueSearchText(searchLeagueText);

    if (!selectedFlag) {
      setFlagLeague([]);
      return;
    }

    try {
      setLoadingSearch(true);
      const response = await $apiV1.get(`/platform/competition`, {
        params: {
          page: 1,
          page_size: 100,
          search: searchLeagueText || undefined,
          country: selectedFlag.short_name,
          sort: "alphabet",
        },
      });
      setFlagLeague(response?.data?.results || []);
    } catch (error) {
      console.error("Error searching leagues:", error);
      setFlagLeague([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const isNationalTeamItem = (item) => {
    const value = String(item?.team_type ?? item?.team_type_display ?? "").toLowerCase();
    return value === "2" || value.includes("national");
  };

  const hasTeamTypeMetadata = (teams = []) =>
    teams.some((item) => {
      const rawType = item?.team_type;
      const displayType = item?.team_type_display;
      const hasRawType = rawType !== undefined && rawType !== null && String(rawType).trim() !== "";
      const hasDisplayType =
        displayType !== undefined &&
        displayType !== null &&
        String(displayType).trim() !== "";
      return hasRawType || hasDisplayType;
    });

  const keepNationalTeamsWhenTyped = (teams = []) => {
    if (!Array.isArray(teams) || teams.length === 0) return [];
    if (!hasTeamTypeMetadata(teams)) return teams;
    return teams.filter(isNationalTeamItem);
  };

  const fetchAllTeamsFromV2 = async (params = {}) => {
    const maxPages = 50;
    let page = 1;
    let totalCount = null;
    let aggregated = [];

    while (page <= maxPages) {
      const response = await $apiV2.get(`/platform/team`, {
        params: {
          ...params,
          page,
        },
      });

      const results = Array.isArray(response?.data?.results) ? response.data.results : [];
      const parsedCount = Number(response?.data?.count);
      if (Number.isFinite(parsedCount) && parsedCount >= 0) {
        totalCount = parsedCount;
      }

      aggregated = [...aggregated, ...results];

      if (results.length === 0) break;
      if (totalCount !== null && aggregated.length >= totalCount) break;

      page += 1;
    }

    return aggregated;
  };

  const fetchFlagLeaguesByCountry = async (flagItem, searchText = "") => {
    if (!flagItem) {
      setFlagLeague([]);
      return [];
    }

    const response = await $apiV1.get(`/platform/competition`, {
      params: {
        country: flagItem.short_name,
        page_size: 100,
        search: searchText || undefined,
        sort: "alphabet",
      },
    });
    const results = response?.data?.results || [];
    setFlagLeague(results);
    return results;
  };

  const fetchFlagTeamsByCountry = async (
    flagItem,
    nextTeamType = selectedTeamType,
    nextSearchText = teamSearchText
  ) => {
    const requestId = ++latestFlagTeamRequestRef.current;
    if (!flagItem) {
      if (requestId === latestFlagTeamRequestRef.current) {
        setTeamLeagueSearch([]);
      }
      return;
    }

    const requestedTeamType = String(nextTeamType || "1");
    const countryCandidates = [];
    const pushCountryCandidate = (value) => {
      if (value === undefined || value === null || value === "") return;
      const normalizedValue = String(value).trim();
      if (!normalizedValue) return;
      if (
        countryCandidates.some(
          (candidate) => String(candidate).toLowerCase() === normalizedValue.toLowerCase()
        )
      ) {
        return;
      }
      countryCandidates.push(value);
    };

    pushCountryCandidate(flagItem?.id);
    pushCountryCandidate(flagItem?.short_name);
    pushCountryCandidate(flagItem?.name);

    const fetchTeams = async (countryValue) => {
      if (!countryValue) return [];
      return fetchAllTeamsFromV2({
        search: nextSearchText || undefined,
        country: countryValue,
        team_type: requestedTeamType,
      });
    };

    try {
      setLoadingSearch(true);
      let teams = [];
      for (const countryCandidate of countryCandidates) {
        const candidateTeams = await fetchTeams(countryCandidate);
        const normalizedCandidateTeams =
          requestedTeamType === "2"
            ? keepNationalTeamsWhenTyped(candidateTeams)
            : candidateTeams;
        teams = normalizedCandidateTeams;
        if (normalizedCandidateTeams.length > 0) break;
      }

      if (teams.length === 0 && requestedTeamType === "2") {
        const searchCandidates = [];
        const pushSearchCandidate = (value) => {
          if (value === undefined || value === null || value === "") return;
          const normalizedValue = String(value).trim();
          if (!normalizedValue) return;
          if (
            searchCandidates.some(
              (candidate) => String(candidate).toLowerCase() === normalizedValue.toLowerCase()
            )
          ) {
            return;
          }
          searchCandidates.push(normalizedValue);
        };

        pushSearchCandidate(flagItem?.name);
        pushSearchCandidate(flagItem?.short_name);

        for (const searchCandidate of searchCandidates) {
          const candidateTeams = await fetchAllTeamsFromV2({
            team_type: requestedTeamType,
            search: searchCandidate,
          });
          const normalizedCandidateTeams = keepNationalTeamsWhenTyped(candidateTeams);
          teams = normalizedCandidateTeams;
          if (normalizedCandidateTeams.length > 0) break;
        }
      }

      if (requestId !== latestFlagTeamRequestRef.current) return;
      setTeamLeagueSearch(teams);
    } catch (error) {
      if (requestId !== latestFlagTeamRequestRef.current) return;
      console.error("Error fetching teams:", error);
      setTeamLeagueSearch([]);
    } finally {
      if (requestId === latestFlagTeamRequestRef.current) {
        setLoadingSearch(false);
      }
    }
  };

  const fetchFlagTeamsByCompetition = async (
    competitionItem,
    nextTeamType = selectedTeamType,
    nextSearchText = teamSearchText
  ) => {
    const requestId = ++latestFlagTeamRequestRef.current;
    if (!competitionItem) {
      if (requestId === latestFlagTeamRequestRef.current) {
        setTeamLeagueSearch([]);
      }
      return;
    }

    const countryCandidates = [];
    const pushCountryCandidate = (value) => {
      if (value === undefined || value === null || value === "") return;
      const normalizedValue = String(value).trim();
      if (!normalizedValue) return;
      if (
        countryCandidates.some(
          (candidate) => String(candidate).toLowerCase() === normalizedValue.toLowerCase()
        )
      ) {
        return;
      }
      countryCandidates.push(value);
    };

    pushCountryCandidate(competitionItem?.country?.id);
    pushCountryCandidate(selectedFlag?.id);
    pushCountryCandidate(competitionItem?.country?.short_name);
    pushCountryCandidate(selectedFlag?.short_name);
    pushCountryCandidate(competitionItem?.country?.name);
    pushCountryCandidate(selectedFlag?.name);

    const fetchTeams = async (countryValue) =>
      fetchAllTeamsFromV2({
        search: nextSearchText || undefined,
        country: countryValue || undefined,
        league: competitionItem?.name || undefined,
        team_type: String(nextTeamType || "1"),
      });

    try {
      setLoadingSearch(true);
      let teams = [];

      if (countryCandidates.length > 0) {
        for (const countryCandidate of countryCandidates) {
          teams = await fetchTeams(countryCandidate);
          if (teams.length > 0) break;
        }
      } else {
        teams = await fetchTeams(undefined);
      }

      // Keep legacy fallback when backend does not apply league/team filters consistently.
      if (teams.length === 0 && Array.isArray(competitionItem?.seasons) && competitionItem?.seasons?.[0]?.id) {
        const seasonResponse = await $apiV1.get(`/platform/season/${competitionItem.seasons[0].id}`);
        teams = seasonResponse?.data?.teams || [];

        if (nextSearchText) {
          const normalizedSearch = String(nextSearchText).toLowerCase();
          teams = teams.filter((item) =>
            String(item?.name || "").toLowerCase().includes(normalizedSearch)
          );
        }

        if (String(nextTeamType) === "2") {
          teams = keepNationalTeamsWhenTyped(teams);
        } else if (String(nextTeamType) === "1") {
          teams = teams.filter((item) => {
            const value = String(item?.team_type ?? item?.team_type_display ?? "").toLowerCase();
            return value !== "2" && !value.includes("national");
          });
        }
      }

      if (requestId !== latestFlagTeamRequestRef.current) return;
      setTeamLeagueSearch(teams);
    } catch (error) {
      if (requestId !== latestFlagTeamRequestRef.current) return;
      console.error("Error fetching teams:", error);
      setTeamLeagueSearch([]);
    } finally {
      if (requestId === latestFlagTeamRequestRef.current) {
        setLoadingSearch(false);
      }
    }
  };

  const handleLeagueSearch = async (item) => {
    setSelectedFlag(item);
    setSelectedLeague(null);
    setTeamSearchText("");
    setLeagueSearchText("");
    setTeamLeagueSearch([]);

    try {
      if (String(selectedTeamType) === "2") {
        setFlagLeague([]);
        await fetchFlagTeamsByCountry(item, "2", "");
        return;
      }

      setLoadingSearch(true);
      await fetchFlagLeaguesByCountry(item);
    } catch (error) {
      console.error("Error fetching leagues:", error);
      setFlagLeague([]);
      setTeamLeagueSearch([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleTeamTypeChange = async (nextType) => {
    const normalizedType = String(nextType || "1");
    if (normalizedType === selectedTeamType) return;

    setSelectedTeamType(normalizedType);

    if (!isFlagModeActive || !selectedFlag) return;

    setSelectedLeague(null);
    setLeagueSearchText("");

    if (normalizedType === "2") {
      setFlagLeague([]);
      setTeamSearchText("");
      setTeamLeagueSearch([]);
      return;
    }

    setTeamSearchText("");
    setTeamLeagueSearch([]);

    try {
      setLoadingSearch(true);
      await fetchFlagLeaguesByCountry(selectedFlag);
    } catch (error) {
      console.error("Error fetching leagues:", error);
      setFlagLeague([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleTeamSearch = async (competitionItem) => {
    setSelectedLeague(competitionItem);
    setTeamSearchText("");
    await fetchFlagTeamsByCompetition(competitionItem, selectedTeamType, "");
  };

  useEffect(() => {
    getFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  useEffect(() => {
    try {
      if (localStorage.getItem(DASHBOARD_TUTORIAL_STORAGE_KEY) !== "1") {
        setShowTutorial(true);
        setTutorialStep(0);
      }
    } catch {
      setShowTutorial(true);
      setTutorialStep(0);
    }
  }, []);

  useEffect(() => {
    if (!showTutorial) return;
    const currentStep = DASHBOARD_TUTORIAL_STEPS[tutorialStep];
    if (!currentStep) return;

    if (activeTab !== currentStep.tab) {
      setActiveTab(currentStep.tab);
    }

    setText("");
    setToggleChecked(false);
    setFlagSearchText("");
    latestFlagTeamRequestRef.current += 1;
    setSelectedFlag(null);
    setFlagLeague([]);
    setLeagueSearchText("");
    setSelectedLeague(null);
    setTeamLeagueSearch([]);
    setTeamSearchText("");
    setListOption(false);
    setFlagSearchResults([]);
    setShowHistoryList(true);
  }, [showTutorial, tutorialStep, activeTab]);

  useEffect(() => {
    if (!showTutorial) return undefined;

    const currentStep = DASHBOARD_TUTORIAL_STEPS[tutorialStep];
    if (!currentStep?.spotlight) {
      setTutorialRect(null);
      return undefined;
    }

    const refs = {
      tabs: tabsRef,
      search: searchFormRef,
      filters: filtersRef,
      history: historyRef,
    };
    const spotlightRef = refs[currentStep.spotlight];

    const updateRect = () => {
      const target = spotlightRef?.current;
      if (!target) {
        setTutorialRect(null);
        return;
      }
      const rect = target.getBoundingClientRect();
      setTutorialRect({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        right: rect.right,
        bottom: rect.bottom,
      });
    };

    const timeoutId = window.setTimeout(updateRect, 70);
    const animationFrameId = window.requestAnimationFrame(updateRect);

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.clearTimeout(timeoutId);
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [showTutorial, tutorialStep, activeTab, showhistoryList, toggleChecked, text, selectedTeamType]);

  useEffect(() => {
    if (!toggleChecked || !selectedFlag) return;

    if (String(selectedTeamType) === "2") {
      fetchFlagTeamsByCountry(selectedFlag, selectedTeamType, teamSearchText);
      return;
    }

    if (!selectedLeague) return;
    fetchFlagTeamsByCompetition(selectedLeague, selectedTeamType, teamSearchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamType, teamSearchText]);

  useEffect(() => {
    const handlePageHistoryChange = () => {
      setPageHistoryVersion((prev) => prev + 1);
    };
    const handleStorage = (event) => {
      if (!event?.key || event.key === "pageHistory") {
        handlePageHistoryChange();
      }
    };

    window.addEventListener(PAGE_HISTORY_UPDATED_EVENT, handlePageHistoryChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(PAGE_HISTORY_UPDATED_EVENT, handlePageHistoryChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (toggleChecked || text.length > 0) return undefined;

    let isMounted = true;

    fetchDashboardHistoryByTab(activeTab, selectedTeamType, {
      force: activeTab === "teams" || activeTab === "competitions",
      visitedSeasonIds: visitedCompetitionSeasonIds,
    }).then((items) => {
      if (!isMounted) return;
      applyHistoryState(activeTab, items);
    });

    return () => {
      isMounted = false;
    };
  }, [activeTab, selectedTeamType, text, toggleChecked, visitedCompetitionSeasonIds]);

  useEffect(() => {
    setShowableHistory(getHistoryByTab(activeTab));
  }, [activeTab, historyList]);

  useEffect(() => {
    const handleGlobalKeydown = (event) => {
      const key = event.key?.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === "k") {
        event.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
        return;
      }
      if (event.key === "Escape") {
        setText("");
        setFlagSearchText("");
        setLeagueSearchText("");
        setTeamSearchText("");
      }
    };

    document.addEventListener("keydown", handleGlobalKeydown);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeydown);
    };
  }, []);

  useEffect(() => {
    if (!toggleChecked && text) {
      getSearchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, activeTab, selectedTeamType, toggleChecked]);

  const getSearchData = async () => {
    const searchTab = activeTab;
    const searchQuery = text;
    const endpoint =
      searchTab === "teams"
        ? `/platform/team`
        : searchTab === "players"
          ? `/platform/player`
          : `/platform/competition`;
    const params = { search: searchQuery };
    if (searchTab === "teams") {
      params.team_type = selectedTeamType;
    }

    const requestId = ++latestSearchRequestRef.current;
    searchAbortRef.current?.abort();
    const ctrl = new AbortController();
    searchAbortRef.current = ctrl;

    setLoadingSearch(true);
    try {
      const data = await $apiV2.get(endpoint, { params, signal: ctrl.signal });
      if (requestId !== latestSearchRequestRef.current) return;

      const results = data.data.results || [];
      trackEntitySearch({
        entityType: searchTab,
        query: searchQuery,
        resultCount: data.data.count,
        results,
        sourceSurface: "dashboard",
        metadata: {
          team_type: searchTab === "teams" ? selectedTeamType : null,
        },
      });

      if (searchTab === "teams") {
        setTeamSearchResults(results);
        setTeamTotalResults(data.data.count);
        setTeamPage(1);
      } else if (searchTab === "players") {
        setPlayerSearchResults(results);
        setPlayerTotalResults(data.data.count);
        setPlayerPage(1);
      } else {
        setCompetitionSearchResults(results);
        setCompetitionTotalResults(data.data.count);
        setCompetitionPage(1);
      }
    } catch (error) {
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
      console.error("Error fetching data:", error);
    } finally {
      if (requestId === latestSearchRequestRef.current) {
        setLoadingSearch(false);
      }
      if (searchAbortRef.current === ctrl) {
        searchAbortRef.current = null;
      }
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setText("");

    if (tab !== "teams" && toggleChecked) {
      setToggleChecked(false);
      setFlagSearchText("");
      resetFlagFlowState();
    }

    if (tab === "teams") {
      setSelectedTeamType("1");
    }
  };

  const isFlagModeActive = activeTab === "teams" && toggleChecked;
  const isNationalFlagMode = isFlagModeActive && selectedTeamType === "2";
  const showDirectNationalTeams = isNationalFlagMode && Boolean(selectedFlag);

  const emptyInput = () => {
    setText("");
  };

  const handleLoadmore = async () => {
    try {
      const response = await $apiV2.get(`/platform/team`, {
        params: {
          page: teamPage + 1,
          search: text,
          team_type: selectedTeamType,
        },
      });
      const newResults = response?.data?.results || [];
      setTeamSearchResults((prevResults) => [...prevResults, ...newResults]);
      setTeamTotalResults(response?.data?.count);
      setTeamPage((prevPage) => prevPage + 1);
    } catch (error) {
      console.error("Error fetching more teams:", error);
    }
  };

  const handleLoadmorePlayers = async () => {
    try {
      const response = await $apiV2.get(`/platform/player`, {
        params: {
          page: playerPage + 1,
          search: text,
        },
      });
      const newResults = response?.data?.results || [];
      setPlayerSearchResults((prevResults) => [...prevResults, ...newResults]);
      setPlayerTotalResults(response?.data?.count);
      setPlayerPage((prevPage) => prevPage + 1);
    } catch (error) {
      console.error("Error fetching more teams:", error);
    }
  };

  const handleLoadmoreCompetitions = async () => {
    try {
      const response = await $apiV2.get(`/platform/competition`, {
        params: {
          page: competitionPage + 1,
          search: text,
        },
      });
      const newResults = response?.data?.results || [];
      setCompetitionSearchResults((prevResults) => [...prevResults, ...newResults]);
      setCompetitionTotalResults(response?.data?.count);
      setCompetitionPage((prevPage) => prevPage + 1);
    } catch (error) {
      console.error("Error fetching more competitions:", error);
    }
  };

  const handleShowHistory = () => {
    setShowHistoryList((prev) => !prev);
  };

  const getFlagModeInputValue = () => {
    if (!selectedFlag) return flagSearchText;
    if (showDirectNationalTeams) return teamSearchText;
    if (!selectedLeague) return leagueSearchText;
    return teamSearchText;
  };

  const getFlagSearchLabel = () => {
    const flagSearchLabel = l(904);
    return flagSearchLabel === "flag search" ? "Flag Search" : flagSearchLabel;
  };

  const getFlagModeInputPlaceholder = () => {
    if (!selectedFlag) return getFlagSearchLabel();
    if (showDirectNationalTeams) return l(906);
    if (!selectedLeague) return l(906);
    return l(906);
  };

  const handleFlagModeInputChange = (e) => {
    if (!selectedFlag) {
      handleFlagSearch(e);
      return;
    }
    if (showDirectNationalTeams) {
      setTeamSearchText(e.target.value);
      return;
    }
    if (!selectedLeague) {
      handleLeague(e);
      return;
    }
    setTeamSearchText(e.target.value);
  };

  const clearFlagModeInput = () => {
    if (!selectedFlag) {
      setFlagSearchText("");
      setFlagSearchResults([]);
      return;
    }
    if (showDirectNationalTeams) {
      setTeamSearchText("");
      return;
    }
    if (!selectedLeague) {
      setLeagueSearchText("");
      handleLeague({ target: { value: "" } });
      return;
    }
    setTeamSearchText("");
  };

  const completeTutorial = () => {
    try {
      localStorage.setItem(DASHBOARD_TUTORIAL_STORAGE_KEY, "1");
    } catch {
      // noop
    }
    setShowTutorial(false);
    setTutorialRect(null);
  };

  const handleTutorialNext = () => {
    if (tutorialStep >= DASHBOARD_TUTORIAL_STEPS.length - 1) {
      completeTutorial();
      return;
    }
    setTutorialStep((prev) => prev + 1);
  };

  const handleTutorialSkip = () => {
    completeTutorial();
  };

  const handleTutorialReplay = () => {
    setTutorialStep(0);
    setTutorialRect(null);
    setShowTutorial(true);
  };

  const tutorialCurrentStep = showTutorial
    ? DASHBOARD_TUTORIAL_STEPS[tutorialStep]
    : null;
  const tutorialIsLastStep = tutorialStep >= DASHBOARD_TUTORIAL_STEPS.length - 1;
  const tutorialHelpBottom =
    typeof window !== "undefined" && window.innerWidth <= 768 ? 12 : 16;
  const tutorialSpotlightPadding = 10;
  const tutorialSpotlightStyle = tutorialRect
    ? {
      left: tutorialRect.left - tutorialSpotlightPadding,
      top: tutorialRect.top - tutorialSpotlightPadding,
      width: tutorialRect.width + tutorialSpotlightPadding * 2,
      height: tutorialRect.height + tutorialSpotlightPadding * 2,
    }
    : null;

  const tutorialCardStyle = (() => {
    const cardWidth = 340;
    const viewportWidth =
      typeof window !== "undefined" ? window.innerWidth : cardWidth + 40;
    const viewportHeight =
      typeof window !== "undefined" ? window.innerHeight : 700;

    if (!tutorialCurrentStep) {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: cardWidth,
      };
    }

    if (tutorialCurrentStep.id === "welcome") {
      if (viewportWidth <= 420) {
        return {
          position: "fixed",
          left: 16,
          right: 16,
          bottom: 16,
          top: "auto",
          transform: "none",
          width: "auto",
        };
      }
      return {
        position: "fixed",
        right: 24,
        bottom: 24,
        width: cardWidth,
        left: "auto",
        top: "auto",
        transform: "none",
      };
    }

    if (!tutorialRect) {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: cardWidth,
      };
    }

    let top = 16;
    let left = tutorialRect.left + tutorialRect.width / 2 - cardWidth / 2;

    if (tutorialCurrentStep.position === "below") {
      top = tutorialRect.bottom + tutorialSpotlightPadding + 18;
    } else if (tutorialCurrentStep.position === "above") {
      top = tutorialRect.top - tutorialSpotlightPadding - 20 - 180;
    }

    left = Math.max(16, Math.min(left, viewportWidth - cardWidth - 16));
    top = Math.max(16, Math.min(top, viewportHeight - 220));

    return {
      position: "fixed",
      top,
      left,
      width: cardWidth,
    };
  })();

  return (
    <MainWrapper>
      <VersionUpdateNotifier />
      <Navigation />

      <HomeSearch>
        <HomeSearchContent>
          <div className="hero-title">
            <h2>
              <span className="line accent">{heroTitleLine1}</span>
              {heroTitleLine2 ? (
                <span className="line normal">{heroTitleLine2}</span>
              ) : null}
            </h2>
            <p className="hero-subtitle">{heroSubtitle}</p>
          </div>

          <WrapSearch>
            <ToggleSearch ref={tabsRef} className={toggleChecked ? "disable" : ""}>
              <span
                className="tab-slider"
                data-active={activeTabIndex}
                aria-hidden="true"
              />
              <TeamsSearch
                className={activeTab === "teams" ? "active" : ""}
                onClick={() => handleTabChange("teams")}
              >
                {l(3)}
              </TeamsSearch>
              <PlayersSearch
                className={activeTab === "players" ? "active" : ""}
                onClick={() => handleTabChange("players")}
              >
                {l(5)}
              </PlayersSearch>
              <CompetitionsSearch
                className={activeTab === "competitions" ? "active" : ""}
                onClick={() => handleTabChange("competitions")}
              >
                {l(1)}
              </CompetitionsSearch>
            </ToggleSearch>

            <SearchForm ref={searchFormRef}>
              <SearchContent>
                <Search>
                  <span className={`search-icon ${loadingSearch ? "loading" : ""}`}>
                    {loadingSearch ? (
                      <Oval
                        secondaryColor={
                          localStorage.getItem("theme") === "light"
                            ? "#4E5255"
                            : "#CFDAE6"
                        }
                        wrapperStyle={{ alignItems: "center" }}
                        width={14}
                        height={14}
                      />
                    ) : (
                      <img src={searchIcon} alt="search icon" />
                    )}
                  </span>
                  <SearchInput>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={isFlagModeActive ? getFlagModeInputPlaceholder() : l(906)}
                      onChange={isFlagModeActive ? handleFlagModeInputChange : (e) => setText(e.target.value)}
                      value={isFlagModeActive ? getFlagModeInputValue() : text}
                    />
                    {!isFlagModeActive && text.length === 0 && (
                      <span className="shortcut-badge" aria-hidden="true">
                        <kbd>{isMacLike ? "⌘" : "Ctrl"}</kbd>
                        <kbd>K</kbd>
                      </span>
                    )}
                    {(isFlagModeActive ? getFlagModeInputValue().length > 0 : text.length > 0) && (
                      <button
                        type="button"
                        className="clear-btn"
                        aria-label="Clear search"
                        onClick={isFlagModeActive ? clearFlagModeInput : emptyInput}
                      >
                        <img src={closeIcon} alt="" />
                      </button>
                    )}
                  </SearchInput>
                </Search>
                {activeTab === "teams" && (
                  <SearchByFlag>
                    <h5>{getFlagSearchLabel()}</h5>
                    <label className="switch">
                      <input type="checkbox" checked={toggleChecked} onChange={handleToggleChange} />
                      <span className="sliderHome round"></span>
                    </label>
                  </SearchByFlag>
                )}
              </SearchContent>
            </SearchForm>
            {activeTab === "teams" && (
              <ToggleSlot ref={filtersRef}>
                <TeamTypeToggle>
                  <TeamTypeOption
                    className={selectedTeamType === "1" ? "active" : ""}
                    onClick={() => handleTeamTypeChange("1")}
                  >
                    {l(1038)}
                  </TeamTypeOption>
                  <TeamTypeOption
                    className={selectedTeamType === "2" ? "active" : ""}
                    onClick={() => handleTeamTypeChange("2")}
                  >
                    {l(1042)}
                  </TeamTypeOption>
                </TeamTypeToggle>
              </ToggleSlot>
            )}
          </WrapSearch>

          <ResultsFrame className={isFlagModeActive ? "flag-mode" : ""}>
            {isFlagModeActive ? (
              <>
                {!selectedFlag && (
                  <Area>
                    <FagsWrap className={(flagSearchText ? flagSearchResults.length : flags?.results?.length) > 8 ? "flagWrapFull" : ""}>
                      {(flagSearchText ? flagSearchResults : flags?.results || []).map((item, index) => (
                        <Flag
                          key={item?.id || index}
                          onClick={() => handleLeagueSearch(item)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            handleLeagueSearch(item);
                          }}
                        >
                          <img src={item?.flag || ""} alt={item.name} />
                          <h3 data-tooltip-id={`country-name-${item?.id || index}`}>
                            {item.name}
                          </h3>
                          <ReactTooltip
                            id={`country-name-${item?.id || index}`}
                            place="bottom"
                            content={item.name}
                          />
                        </Flag>
                      ))}
                    </FagsWrap>

                    {flags.count > 100 && (
                      pageSize >= flags.count ? (
                        <button onClick={() => setPageSize(100)}>{l(908)}</button>
                      ) : (
                        <button
                          onClick={() =>
                            setPageSize((prev) =>
                              Math.min(Number(flags.count || 0), Number(prev || 0) + FLAG_PAGE_SIZE_STEP)
                            )
                          }
                        >
                          {l(907)}
                        </button>
                      )
                    )}
                  </Area>
                )}

                {selectedFlag && !selectedLeague && !isNationalFlagMode && (
                  <FullAreaWrap>
                    <Button title={"back"} onClick={() => setSelectedFlag(null)} />
                    <Area>
                      <ChooseGrid>
                        <span onClick={() => setListOption(false)}>
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? listLight
                                : listDark
                            }
                            alt="List view"
                          />
                        </span>
                        <span onClick={() => setListOption(true)}>
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? gridLight
                                : gridDark
                            }
                            alt="Grid view"
                          />
                        </span>
                      </ChooseGrid>

                      {!listOption ? (
                        <ListWrap>
                          {flagLeague.map((item, index) => (
                            <FlagList
                              key={item?.id || index}
                              onClick={() => handleTeamSearch(item)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                handleTeamSearch(item);
                              }}
                            >
                              <img
                                src={
                                  item.logo
                                    ? item.logo
                                    : localStorage.getItem("theme") === "light"
                                      ? placeholderLight
                                      : placeholder
                                }
                                alt={item.name}
                              />
                              <h3>{item.name}</h3>
                            </FlagList>
                          ))}
                        </ListWrap>
                      ) : (
                        <FagsWrap style={{ marginTop: "10px" }}>
                          {flagLeague.map((item, index) => (
                            <Flag
                              key={item?.id || index}
                              onClick={() => handleTeamSearch(item)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                handleTeamSearch(item);
                              }}
                            >
                              <img
                                src={
                                  item.logo
                                    ? item.logo
                                    : localStorage.getItem("theme") === "light"
                                      ? placeholderLight
                                      : placeholder
                                }
                                alt={item.name}
                              />
                              <h3 data-tooltip-id={`competition-name-${item?.id || index}`}>
                                {item.name}
                              </h3>
                              <ReactTooltip
                                id={`competition-name-${item?.id || index}`}
                                place="bottom"
                                content={item.name}
                              />
                            </Flag>
                          ))}
                        </FagsWrap>
                      )}
                    </Area>
                  </FullAreaWrap>
                )}

                {(selectedLeague || showDirectNationalTeams) && (
                  <FullAreaWrap>
                    <Button
                      title={"back"}
                      onClick={() => {
                        if (showDirectNationalTeams && !selectedLeague) {
                          setSelectedFlag(null);
                          setSelectedLeague(null);
                          setFlagLeague([]);
                          setTeamLeagueSearch([]);
                          setTeamSearchText("");
                          setLeagueSearchText("");
                          return;
                        }
                        setSelectedLeague(null);
                      }}
                    />
                    <Area>
                      <FagsWrap
                        className={teamLeagueSearch?.length > 8 ? "flagWrapFull" : ""}
                      >
                        {teamLeagueSearch?.length > 0 ? (
                          teamLeagueSearch.map((teamItem, index) => (
                            <Flag
                              key={teamItem?.id || index}
                              onClick={() =>
                                openTeam(teamItem, {
                                  country_id: selectedFlag?.id,
                                  country_name: selectedFlag?.name,
                                  competition_id: selectedLeague?.id,
                                  competition_name: selectedLeague?.name,
                                  search_mode: "flag",
                                })
                              }
                              onContextMenu={() =>
                                openTeam(
                                  teamItem,
                                  {
                                    country_id: selectedFlag?.id,
                                    country_name: selectedFlag?.name,
                                    competition_id: selectedLeague?.id,
                                    competition_name: selectedLeague?.name,
                                    search_mode: "flag",
                                  },
                                  true
                                )
                              }
                            >
                              <img
                                src={
                                  teamItem.logo
                                    ? teamItem.logo
                                    : localStorage.getItem("theme") === "light"
                                      ? placeholderLight
                                      : placeholder
                                }
                                alt={teamItem.name}
                              />
                              <h3 data-tooltip-id={`team-name-${teamItem?.id || index}`}>
                                {teamItem.name}
                              </h3>
                              <ReactTooltip
                                id={`team-name-${teamItem?.id || index}`}
                                place="bottom"
                                content={teamItem.name}
                              />
                            </Flag>
                          ))
                        ) : (
                          <Flag>
                            <h3>{l(314)}</h3>
                          </Flag>
                        )}
                      </FagsWrap>
                    </Area>
                  </FullAreaWrap>
                )}
              </>
            ) : text.length > 0 ? (
              <SearchResult>
                <ul className={activeTab === "competitions" ? "competition-results" : ""}>
                  {activeTab === "teams" &&
                    teamSearchResults?.map((teamItem) => (
                      <li
                        className="result-item"
                        key={teamItem.id}
                        onClick={() => openTeam(teamItem)}
                        onContextMenu={() => openTeam(teamItem, {}, true)}
                      >
                        <a>
                          <img
                            src={
                              teamItem.logo
                                ? teamItem.logo
                                : localStorage.getItem("theme") === "light"
                                  ? placeholderLight
                                  : placeholder
                            }
                            alt={teamItem.name}
                          />
                          <h5>{teamItem.name}</h5>
                        </a>
                      </li>
                    ))}

                  {teamTotalResults > 0 &&
                    teamTotalResults > teamSearchResults?.length &&
                    activeTab === "teams" && (
                      <button className="loadMoreTeams" onClick={() => handleLoadmore()}>
                        {l(907)}
                      </button>
                    )}

                  {activeTab === "players" &&
                    playerSearchResults?.map((player) => (
                      <li
                        className="result-item"
                        key={player.id}
                        onClick={() => openPlayer(player)}
                        onContextMenu={() => openPlayer(player, true)}
                      >
                        <a>
                          <img
                            src={getMediaUrl(player?.nationality?.[0]?.flag) || team}
                            alt={player.logo}
                          />
                          <h5>
                            {player.display_name || `${player.name} ${player.surname}`}{" "}
                            {player?.team?.name ? `(${player.team.name})` : ""}
                          </h5>
                        </a>
                      </li>
                    ))}

                  {playerTotalResults > 0 &&
                    playerTotalResults > playerSearchResults?.length &&
                    activeTab === "players" && (
                      <button className="loadMoreTeams" onClick={() => handleLoadmorePlayers()}>
                        {l(907)}
                      </button>
                    )}

                  {activeTab === "competitions" &&
                    competitionSearchResults?.map((competition, index) => {
                      const preferredSeason = getPreferredSeason(competition?.seasons);
                      const sortedSeasons = getSortedSeasons(competition?.seasons);
                      const hasPreferredSeason = Boolean(preferredSeason?.id);
                      return (
                        <li key={competition.id} className={`competition-item ${hasPreferredSeason ? "" : "disabled"}`}>
                          <div className="competition-group comp-group">
                            <button
                              type="button"
                              className="competition-header"
                              onClick={() => openCompetitionSeason(preferredSeason, false, competition)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                openCompetitionSeason(preferredSeason, true, competition);
                              }}
                              disabled={!hasPreferredSeason}
                            >
                              <div className="competition-logo">
                                <img
                                  src={
                                    competition.logo
                                      ? competition.logo
                                      : localStorage.getItem("theme") === "light"
                                        ? placeholderLight
                                        : placeholder
                                  }
                                  alt={competition.name}
                                />
                              </div>
                              <div className="competition-info">
                                <div className="competition-country">
                                  {competition?.country?.name || ""}
                                </div>
                                <div className="competition-name">{competition.name}</div>
                              </div>
                            </button>

                            {sortedSeasons.length > 0 && (
                              <div className="seasons-row">
                                {sortedSeasons.map((season, idx) => (
                                  <button
                                    key={season?.id || `${competition.id}-${idx}`}
                                    type="button"
                                    className={`season-pill ${
                                      String(preferredSeason?.id) === String(season?.id)
                                        ? "current"
                                        : ""
                                    }`}
                                    onClick={() => openCompetitionSeason(season, false, competition)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      openCompetitionSeason(season, true, competition);
                                    }}
                                    disabled={!season?.id}
                                  >
                                    {getSeasonLabel(season)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {index < competitionSearchResults.length - 1 && (
                            <div className="divider" />
                          )}
                        </li>
                      );
                    })}

                  {competitionTotalResults > 0 &&
                    competitionTotalResults > competitionSearchResults?.length &&
                    activeTab === "competitions" && (
                      <button className="loadMoreTeams" onClick={() => handleLoadmoreCompetitions()}>
                        {l(907)}
                      </button>
                    )}
                </ul>
              </SearchResult>
            ) : (
              <HistoryList ref={historyRef}>
                <HistoryHeader>
                  <h3>{l(909)}</h3>
                  <img
                    src={showhistoryList ? eyeClosed : eyeOpen}
                    alt={showhistoryList ? eyeClosed : eyeOpen}
                    onClick={() => handleShowHistory()}
                  />
                </HistoryHeader>
                {showhistoryList && (
                  <div className="history-container">
                    <ul>
                      {showableHistory?.map((item) => {
                        const entityType = Number(item?.entity_type ?? item?.entity?.entity_type ?? 0);
                        const isTeam = entityType === 2;
                        const isPlayer = entityType === 1;
                        const isCompetition = entityType === 3;
                        const looksLikeImagePath = (value) => {
                          if (typeof value !== "string") return false;
                          const trimmed = value.trim();
                          if (!trimmed) return false;

                          const normalized = trimmed.toLowerCase();
                          const hasImageExtension = /\.(svg|png|jpe?g|webp|gif|bmp|ico)([?#].*)?$/.test(normalized);
                          const hasLikelyMediaSegment =
                            normalized.includes("/media/") ||
                            normalized.includes("/uploads/") ||
                            normalized.includes("/storage/");

                          if (normalized.startsWith("data:image/") || normalized.startsWith("blob:")) {
                            return true;
                          }
                          if (/^https?:\/\//.test(normalized) || normalized.startsWith("//")) {
                            return true;
                          }
                          if (trimmed.startsWith("/")) {
                            return true;
                          }

                          return hasImageExtension || hasLikelyMediaSegment;
                        };

                        const competitionLogoCandidates = [
                          item?.logo,
                          item?.entity?.competition?.logo,
                          item?.entity?.logo,
                        ];
                        const resolvedCompetitionLogo = competitionLogoCandidates.find(
                          looksLikeImagePath
                        );
                        const isTeamHistoryItem = isTeam || activeTab === "teams";
                        const teamLogo =
                          item?.entity?.logo ||
                          item?.logo ||
                          item?.entity?.team_logo ||
                          item?.entity?.team?.logo;
                        const fallbackImage =
                          localStorage.getItem("theme") === "light"
                            ? placeholderLight
                            : placeholder;
                        const historyImageSrc = isTeamHistoryItem
                          ? teamLogo
                            ? teamLogo
                            : fallbackImage
                          : (isCompetition || activeTab === "competitions")
                            ? resolvedCompetitionLogo || fallbackImage
                          : item?.entity?.logo
                            ? getMediaUrl(item?.entity?.logo)
                            : getMediaUrl(item?.entity?.nationality?.[0]?.flag) || fallbackImage;
                        const historyImageAlt = item?.entity?.name || item?.entity?.logo || "history image";
                        const seasonId = getHistoryCompetitionSeasonId(
                          item,
                          visitedCompetitionSeasonIds
                        );
                        return (
                          <li
                            className="result-item history-item"
                            key={item.id}
                            onClick={() => {
                              if (activeTab === "teams" || isTeam) {
                                navigate(`/tournaments/team/${item?.entity?.id}`);
                              } else if (activeTab === "players" || isPlayer) {
                                navigate(`/tournaments/player/${item?.entity?.id}`);
                              } else if ((activeTab === "competitions" || isCompetition) && seasonId) {
                                navigate(`/tournaments/competition/${seasonId}`);
                              }
                            }}
                            onContextMenu={() => {
                              if (activeTab === "teams" || isTeam) {
                                window.open(`/tournaments/team/${item?.entity?.id}`);
                              } else if (activeTab === "players" || isPlayer) {
                                window.open(`/tournaments/player/${item?.entity?.id}`);
                              } else if ((activeTab === "competitions" || isCompetition) && seasonId) {
                                window.open(`/tournaments/competition/${seasonId}`);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <img src={historyImageSrc} alt={historyImageAlt} />
                            <h5>
                              {item?.entity?.display_name || [item?.entity?.name, item?.entity?.surname].filter(Boolean).join(" ")}
                            </h5>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </HistoryList>
            )}
          </ResultsFrame>
        </HomeSearchContent>
      </HomeSearch>

      {!showTutorial && (
        <button
          type="button"
          onClick={handleTutorialReplay}
          style={{
            position: "fixed",
            left: "50%",
            bottom: tutorialHelpBottom,
            transform: "translateX(-50%)",
            zIndex: 20,
            background: "transparent",
            border: "none",
            color: "#7a7a7a",
            fontSize: 13,
            lineHeight: 1.3,
            fontFamily: DASHBOARD_TUTORIAL_FONT_MEDIUM,
            fontWeight: 500,
            cursor: "pointer",
            padding: "4px 8px",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          {l(1678) || "Help"}
        </button>
      )}

      {showTutorial && tutorialCurrentStep && (
        <>
          {tutorialCurrentStep.spotlight && tutorialRect ? (
            <svg
              style={{
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "auto",
                zIndex: 50,
              }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <mask id="dashboard-tutorial-spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect
                    x={tutorialRect.left - tutorialSpotlightPadding}
                    y={tutorialRect.top - tutorialSpotlightPadding}
                    width={tutorialRect.width + tutorialSpotlightPadding * 2}
                    height={tutorialRect.height + tutorialSpotlightPadding * 2}
                    rx="12"
                    fill="black"
                  />
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(0,0,0,0.78)"
                mask="url(#dashboard-tutorial-spotlight-mask)"
              />
            </svg>
          ) : (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: _isLightTheme ? "rgba(15,17,26,0.55)" : "rgba(0,0,0,0.82)",
                zIndex: 50,
                pointerEvents: "auto",
              }}
            />
          )}

          {tutorialSpotlightStyle && (
            <div
              style={{
                position: "fixed",
                ...tutorialSpotlightStyle,
                borderRadius: 14,
                border: `2px solid ${DASHBOARD_TUTORIAL_ACCENT}`,
                boxShadow: `0 0 0 1px ${DASHBOARD_TUTORIAL_ACCENT}44, 0 0 24px ${DASHBOARD_TUTORIAL_ACCENT}66, 0 0 48px ${DASHBOARD_TUTORIAL_ACCENT}22`,
                zIndex: 51,
                pointerEvents: "none",
                animation: "dashboardTutorialPulse 2s ease-in-out infinite",
              }}
            />
          )}

          <div
            key={tutorialCurrentStep.id}
            style={{
              ...tutorialCardStyle,
              zIndex: 60,
              background: DASHBOARD_TUTORIAL_CARD_BG,
              border: _isLightTheme ? "1px solid #E5E7EB" : `1px solid ${DASHBOARD_TUTORIAL_ACCENT}33`,
              borderRadius: 20,
              padding: "28px 28px 22px",
              boxShadow: _isLightTheme
                ? "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
                : `0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px ${DASHBOARD_TUTORIAL_ACCENT}22, inset 0 1px 0 ${DASHBOARD_TUTORIAL_ACCENT}11`,
              fontFamily: DASHBOARD_TUTORIAL_FONT_BASE,
            }}
          >
            {tutorialCurrentStep.id !== "welcome" &&
              tutorialRect &&
              tutorialCurrentStep.position === "below" && (
                <div
                  style={{
                    position: "absolute",
                    top: -10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "10px solid transparent",
                    borderRight: "10px solid transparent",
                    borderBottom: `10px solid ${DASHBOARD_TUTORIAL_ACCENT}55`,
                  }}
                />
            )}
            {tutorialCurrentStep.id !== "welcome" &&
              tutorialRect &&
              tutorialCurrentStep.position === "above" && (
                <div
                  style={{
                    position: "absolute",
                    bottom: -10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "10px solid transparent",
                    borderRight: "10px solid transparent",
                    borderTop: `10px solid ${DASHBOARD_TUTORIAL_ACCENT}55`,
                  }}
                />
            )}

            <div
              style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}
            >
              {DASHBOARD_TUTORIAL_STEPS.map((stepItem, index) => (
                <div
                  key={stepItem.id}
                  style={{
                    height: 3,
                    flex: 1,
                    borderRadius: 99,
                    background:
                      index <= tutorialStep
                        ? DASHBOARD_TUTORIAL_ACCENT
                        : DASHBOARD_TUTORIAL_STEP_INACTIVE,
                    transition: "background 0.4s",
                  }}
                />
              ))}
            </div>

            {tutorialCurrentStep.id === "welcome" && (
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: `linear-gradient(135deg, ${DASHBOARD_TUTORIAL_ACCENT}33, ${DASHBOARD_TUTORIAL_ACCENT}11)`,
                  border: `1px solid ${DASHBOARD_TUTORIAL_ACCENT}55`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 24,
                  marginBottom: 16,
                }}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    filter:
                      "drop-shadow(0 0 4px rgba(255, 218, 0, 0.55)) drop-shadow(0 0 8px rgba(174, 119, 20, 0.35))",
                  }}
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient
                      id="dashboardTutorialBoltGradient"
                      x1="15.5"
                      y1="1.5"
                      x2="6.5"
                      y2="22.5"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0%" stopColor="#FFDA00" />
                      <stop offset="100%" stopColor="#AE7714" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M13.8 1.5L5.5 11.8h5.5L9.9 22.5l8.6-12h-5L13.8 1.5z"
                    fill="url(#dashboardTutorialBoltGradient)"
                    stroke="#FFF4B3"
                    strokeWidth="0.4"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}

            <h2
              style={{
                margin: "0 0 10px",
                fontSize: tutorialCurrentStep.id === "welcome" ? 22 : 18,
                fontWeight: 800,
                fontFamily: DASHBOARD_TUTORIAL_FONT_BOLD,
                color: DASHBOARD_TUTORIAL_TITLE_COLOR,
                lineHeight: 1.25,
                whiteSpace: "pre-line",
                letterSpacing: -0.3,
              }}
            >
              {tutorialCurrentStep.title}
            </h2>

            <p
              style={{
                margin: "0 0 24px",
                fontSize: 13.5,
                fontFamily: DASHBOARD_TUTORIAL_FONT_BASE,
                color: DASHBOARD_TUTORIAL_BODY_COLOR,
                lineHeight: 1.6,
              }}
            >
              {tutorialCurrentStep.body}
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <button
                type="button"
                onClick={handleTutorialSkip}
                style={{
                  background: "transparent",
                  border: "none",
                  color: DASHBOARD_TUTORIAL_SKIP_COLOR,
                  fontSize: 13,
                  cursor: "pointer",
                  padding: "6px 0",
                  fontFamily: DASHBOARD_TUTORIAL_FONT_MEDIUM,
                  fontWeight: 500,
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                Skip tour
              </button>

              <button
                type="button"
                onClick={handleTutorialNext}
                style={{
                  background: DASHBOARD_TUTORIAL_ACCENT,
                  border: "none",
                  color: DASHBOARD_TUTORIAL_BTN_TEXT,
                  fontFamily: DASHBOARD_TUTORIAL_FONT_BOLD,
                  fontWeight: 800,
                  fontSize: 13.5,
                  padding: "10px 24px",
                  borderRadius: 10,
                  cursor: "pointer",
                  letterSpacing: 0.3,
                  boxShadow: `0 4px 20px ${DASHBOARD_TUTORIAL_ACCENT}55`,
                }}
              >
                {tutorialIsLastStep ? "Got it" : "Next →"}
              </button>
            </div>
          </div>

          <style>
            {`
              @keyframes dashboardTutorialPulse {
                0%,100% { box-shadow: 0 0 0 1px ${DASHBOARD_TUTORIAL_ACCENT}44, 0 0 20px ${DASHBOARD_TUTORIAL_ACCENT}55, 0 0 40px ${DASHBOARD_TUTORIAL_ACCENT}22; }
                50% { box-shadow: 0 0 0 2px ${DASHBOARD_TUTORIAL_ACCENT}88, 0 0 32px ${DASHBOARD_TUTORIAL_ACCENT}99, 0 0 64px ${DASHBOARD_TUTORIAL_ACCENT}44; }
              }
            `}
          </style>
        </>
      )}
    </MainWrapper>
  );
};

export default Index;
